import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { apiFetch } from "../api/client";
import type { StudentAnswerDetail, SubmissionDetail } from "../api/types";
import { useFetch } from "../hooks/useFetch";
import { ErrorBanner } from "../components/ErrorBanner";
import { QuestionImages } from "../components/QuestionImages";
import { LoadingState, PageHeader, StatusBadge } from "../components/ui";

interface DetailResponse {
  submission: SubmissionDetail;
}

export function SubmissionGradingPage() {
  const params = useParams<{ submissionId: string }>();
  const submissionId = params.submissionId!;
  const { data, loading, error, refetch } = useFetch<DetailResponse>(
    `/admin/submissions/${submissionId}`,
  );
  const [actionError, setActionError] = useState<Error | null>(null);

  if (loading) return <LoadingState />;
  if (error) return <ErrorBanner error={error} />;
  if (!data) return <p>Not found.</p>;

  const s = data.submission;

  async function gradeAnswer(
    answerId: string,
    patch: { manual_score?: number | null; teacher_feedback?: string },
  ) {
    setActionError(null);
    try {
      await apiFetch(`/admin/student-answers/${answerId}/grade`, {
        method: "PUT",
        body: JSON.stringify(patch),
      });
      refetch();
    } catch (e) {
      setActionError(e instanceof Error ? e : new Error(String(e)));
    }
  }

  async function markGraded(teacherComment: string | null) {
    setActionError(null);
    try {
      await apiFetch(`/admin/submissions/${submissionId}/mark-graded`, {
        method: "POST",
        body: JSON.stringify(
          teacherComment ? { teacher_comment: teacherComment } : {},
        ),
      });
      refetch();
    } catch (e) {
      setActionError(e instanceof Error ? e : new Error(String(e)));
    }
  }

  async function setOverride(
    score: number | null,
    reason: string | null,
  ) {
    setActionError(null);
    try {
      await apiFetch(`/admin/submissions/${submissionId}/score-override`, {
        method: "PUT",
        body: JSON.stringify({
          score_override: score,
          score_override_reason: reason,
        }),
      });
      refetch();
    } catch (e) {
      setActionError(e instanceof Error ? e : new Error(String(e)));
    }
  }

  return (
    <section>
      <PageHeader
        title={`Grading: ${s.user.username}`}
        subtitle={
          <span>
            {s.test.title} · <StatusBadge status={s.status} />
          </span>
        }
      />
      <ErrorBanner error={actionError} />
      <div className="card">
        <p style={{ margin: 0 }}>
          <strong>Auto:</strong> {s.total_auto_score ?? "—"} ·{" "}
          <strong>Manual:</strong> {s.total_manual_score ?? "—"} ·{" "}
          <strong>Computed total:</strong> {s.total_score ?? "—"} ·{" "}
          <strong>Final:</strong> {s.final_score ?? "—"} / {s.max_score}
        </p>
        {s.auto_submitted_at && (
          <p style={{ margin: "0.5rem 0 0" }}>
            <em>
              Auto-submitted by the timer at {s.auto_submitted_at}
            </em>
          </p>
        )}
      </div>

      <ol className="answers">
        {s.answers.map((a) => (
          <AnswerRow
            key={a.id}
            answer={a}
            disabled={s.status === "graded"}
            onSave={(patch) => void gradeAnswer(a.id, patch)}
          />
        ))}
      </ol>

      <MarkGradedForm
        existingComment={s.teacher_comment}
        disabled={s.status !== "submitted"}
        onSubmit={markGraded}
      />

      <ScoreOverrideForm
        currentOverride={s.score_override}
        currentReason={s.score_override_reason}
        maxScore={s.max_score}
        onSubmit={setOverride}
      />
    </section>
  );
}

function AnswerRow({
  answer,
  disabled,
  onSave,
}: {
  answer: StudentAnswerDetail;
  disabled: boolean;
  onSave: (patch: {
    manual_score?: number | null;
    teacher_feedback?: string;
  }) => void;
}) {
  const [manualScore, setManualScore] = useState(
    answer.manual_score?.toString() ?? "",
  );
  const [feedback, setFeedback] = useState(answer.teacher_feedback ?? "");

  useEffect(() => {
    setManualScore(answer.manual_score?.toString() ?? "");
    setFeedback(answer.teacher_feedback ?? "");
  }, [answer.manual_score, answer.teacher_feedback]);

  const isMcq = answer.question.type === "mcq";
  const selected = new Set(answer.selected_option_ids);

  function save() {
    const patch: {
      manual_score?: number | null;
      teacher_feedback?: string;
    } = {};
    if (manualScore === "") {
      // Empty input clears the manual score (back to auto for MCQ, ungraded
      // for free-text).
      if (answer.manual_score !== null) patch.manual_score = null;
    } else {
      const parsed = Number(manualScore);
      if (!Number.isNaN(parsed)) {
        patch.manual_score = parsed;
      }
    }
    patch.teacher_feedback = feedback;
    onSave(patch);
  }

  return (
    <li className="answer">
      <p>
        <strong>{answer.question.prompt}</strong>{" "}
        <small>
          ({answer.question.type}, {answer.question.points} pts ·{" "}
          <strong>final {answer.final_score}</strong>)
        </small>
      </p>
      <QuestionImages images={answer.question.images} kind="admin" />

      {isMcq ? (
        <>
          <ul className="options">
            {answer.question.answer_options.map((o) => {
              const wasSelected = selected.has(o.id);
              return (
                <li key={o.id}>
                  {wasSelected ? "[x]" : "[ ]"} {o.text}{" "}
                  {o.is_correct && (
                    <small style={{ color: "green" }}>(correct)</small>
                  )}
                </li>
              );
            })}
          </ul>
          <p>
            <strong>Auto score:</strong> {answer.auto_score ?? "—"} /{" "}
            {answer.question.points}
          </p>
        </>
      ) : (
        <blockquote className="free-text">
          {answer.text_answer ?? <em>No answer</em>}
        </blockquote>
      )}

      {/* Phase 13: manual_score is allowed on any answer type (override of
          auto for MCQ, primary grade for free-text). Empty input clears it. */}
      <label className="field-block">
        Manual score (leave blank to use auto):
        <input
          type="number"
          min={0}
          max={answer.question.points}
          value={manualScore}
          onChange={(e) => setManualScore(e.target.value)}
          disabled={disabled}
        />
      </label>
      <label className="field-block">
        Feedback:
        <textarea
          className="admin-textarea"
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          disabled={disabled}
        />
      </label>
      <button onClick={save} disabled={disabled}>
        Save
      </button>
    </li>
  );
}

function MarkGradedForm({
  existingComment,
  disabled,
  onSubmit,
}: {
  existingComment: string | null;
  disabled: boolean;
  onSubmit: (teacherComment: string | null) => void;
}) {
  const [comment, setComment] = useState(existingComment ?? "");
  return (
    <div className="inline-form admin-form">
      <fieldset>
        <legend>Finalize</legend>
        <label className="field-block">
          Teacher comment
          <textarea
            className="admin-textarea"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            disabled={disabled}
          />
        </label>
        <button
          onClick={() => onSubmit(comment.trim() || null)}
          disabled={disabled}
        >
          Mark as graded
        </button>
      </fieldset>
    </div>
  );
}

function ScoreOverrideForm({
  currentOverride,
  currentReason,
  maxScore,
  onSubmit,
}: {
  currentOverride: number | null;
  currentReason: string | null;
  maxScore: number;
  onSubmit: (score: number | null, reason: string | null) => void;
}) {
  const [scoreStr, setScoreStr] = useState(
    currentOverride !== null ? String(currentOverride) : "",
  );
  const [reason, setReason] = useState(currentReason ?? "");

  useEffect(() => {
    setScoreStr(currentOverride !== null ? String(currentOverride) : "");
    setReason(currentReason ?? "");
  }, [currentOverride, currentReason]);

  function apply() {
    if (scoreStr === "") {
      onSubmit(null, null);
      return;
    }
    const parsed = Number(scoreStr);
    if (Number.isNaN(parsed)) return;
    onSubmit(parsed, reason.trim() || null);
  }

  function clear() {
    setScoreStr("");
    setReason("");
    onSubmit(null, null);
  }

  return (
    <div className="inline-form admin-form">
      <fieldset>
        <legend>Score override (principal)</legend>
        <p>
          <small>
            Overrides the computed final score. Auto + manual totals are kept
            for transparency. Leave blank to use the computed score.
          </small>
        </p>
        <label className="field-block">
          Override score (0 – {maxScore})
          <input
            className="field-wide"
            type="number"
            min={0}
            max={maxScore}
            step="0.5"
            value={scoreStr}
            onChange={(e) => setScoreStr(e.target.value)}
          />
        </label>
        <label className="field-block">
          Reason
          <textarea
            className="admin-textarea"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why was the score adjusted?"
          />
        </label>
        <button onClick={apply}>Apply override</button>{" "}
        <button onClick={clear} type="button">
          Clear override
        </button>
      </fieldset>
    </div>
  );
}
